import React, { useState, useContext, useEffect } from "react";
import { TextField, Button, InputAdornment, IconButton, Box, Typography } from "@mui/material";
import "./LoginModule.css";
import { useNavigate, useSearchParams } from "react-router-dom";
import { userLogin, startOAuthLogin, startCompanySSO, getAuthOptions } from "../../Api/Login";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Loader from "../Loader/Loader";
import { AuthContext } from "../../Layout/AuthContext";
import { validateAndHandleEmailChange, DEFAULT_BLOCKED_DOMAINS } from "../../Utils/emailValidation";
import { isTrinityPortal, trinityProductOrigin } from "../../Utils/adminUserTypes";
import { clearStoragePreservingDeepLink } from "../../Utils/postAuthRedirect";
import { applyLoginSession } from "./applyLoginSession";

const AUTH_FLAGS_OFF = {
  company_sso: { sign_in: false, sign_up: false },
  oauth: { sign_in: false, sign_up: false },
  google: { sign_in: false, sign_up: false },
  microsoft: { sign_in: false, sign_up: false },
  apple: { sign_in: false, sign_up: false },
};

const signInVisible = (options, key) => {
  if (!options) return false;
  if (key === "company_sso") return !!options.company_sso?.sign_in;
  return !!options.oauth?.sign_in && !!options[key]?.sign_in;
};

const LoginModule = () => {
  const { login } = useContext(AuthContext);
  const [domain, setDomain] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [filledUp, setFilledUp] = useState(false);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [authOptions, setAuthOptions] = useState(null);
  const [authReady, setAuthReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getAuthOptions()
      .then((res) => {
        if (!cancelled) setAuthOptions(res?.data || AUTH_FLAGS_OFF);
      })
      .catch(() => {
        if (!cancelled) setAuthOptions(AUTH_FLAGS_OFF);
      })
      .finally(() => {
        if (!cancelled) setAuthReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const oauthError = searchParams.get('oauth_error');
    if (!oauthError) return;
    const messages = {
      no_account: 'No Zavior account exists for this identity. Ask an admin to invite you first.',
      inactive: 'This account is inactive.',
      unverified: 'This email is not verified.',
      subject_conflict: 'This identity is already linked to a different account.',
      missing_email: 'The identity provider did not return a verified email.',
      signin_disabled: 'This sign-in method is turned off.',
    };
    toast.error(messages[oauthError] || 'Sign-in failed. Please try again.');
  }, [searchParams]);


  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };
  const handleClick = () => {
    zaviorLogin();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!error && filledUp) {
      zaviorLogin();
    }
  };
  
  /* Handles the change event for the "Email Address" field. */
  const handleEmailChange = (e) => {
    validateAndHandleEmailChange(e, setDomain, setError, setFilledUp, {
      allowFreeEmail: true,
      blockedDomains: DEFAULT_BLOCKED_DOMAINS
    });
  };
  /* Handles the change event for the "Password" field. */
  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    setFilledUp(!!value);
  };

  /* login functionality */
  const zaviorLogin = async () => {
    try {
      setLoading(true);
      const request_payload = {
        email: domain,
        password: password,
      };
  
      const response = await userLogin(request_payload);
      const ok = await applyLoginSession({ response, login, navigate });
      if (ok) {
        setDomain("");
        setPassword("");
        setFilledUp(false);
      }
    } catch (err) {
      setLoading(false);
      toast.error(err);
      toast.error(err?.response?.data?.error);
    } finally {
      setLoading(false);
    }
  };
  

  const handleForgetPassword = () => {
    if (isTrinityPortal()) {
      window.location.href = `${trinityProductOrigin()}/reset/password`;
      return;
    }
    navigate("/reset/password");
  };
  const handleUserRegistration = () => {
    navigate("/register");
    // Keep a Complete Now deep link if the user arrived via an email and
    // then chose Register instead of Login.
    clearStoragePreservingDeepLink();
  };

  const startProvider = async (provider) => {
    try {
      setLoading(true);
      const response = await startOAuthLogin(provider);
      const url = response?.data?.authorize_url;
      if (!url) {
        toast.error('Sign-in is not configured yet.');
        return;
      }
      window.location.href = url;
    } catch (err) {
      if (!err?.message) toast.error('Failed to start sign-in');
    } finally {
      setLoading(false);
    }
  };

  const handleCompanySSO = async () => {
    if (!domain) {
      toast.error('Enter your work email first, then click Company SSO.');
      return;
    }
    try {
      setLoading(true);
      const response = await startCompanySSO(domain);
      const url = response?.data?.authorize_url;
      if (!url) {
        toast.error('Company SSO is not configured for this email.');
        return;
      }
      window.location.href = url;
    } catch (err) {
      if (!err?.message) toast.error('Company SSO is not configured for this email');
    } finally {
      setLoading(false);
    }
  };

  // Field styling following cursorrules standards
  const fieldStyles = {
    container: {
      mb: 3
    },
    label: {
      color: '#374151',
      fontWeight: 600,
      fontSize: '14px',
      mb: 1,
      display: 'block'
    },
    textField: {
      '& .MuiOutlinedInput-root': {
        backgroundColor: '#FFFFFF',
        borderRadius: '8px',
        height: '48px',
        '& fieldset': {
          borderColor: '#D1D5DB',
          borderWidth: '1px',
        },
        '&:hover fieldset': {
          borderColor: '#9CA3AF',
        },
        '&.Mui-focused fieldset': {
          borderColor: '#1D3DFC',
          borderWidth: '2px',
        },
      },
      '& .MuiInputBase-input': {
        color: '#374151',
        fontSize: '14px',
        '&::placeholder': {
          color: '#9CA3AF',
          opacity: 1,
        },
      },
    }
  };

  const showGoogle = authReady && signInVisible(authOptions, "google");
  const showMicrosoft = authReady && signInVisible(authOptions, "microsoft");
  const showApple = authReady && signInVisible(authOptions, "apple");
  const showCompanySso = authReady && signInVisible(authOptions, "company_sso");
  const showProviderSignIn = showGoogle || showMicrosoft || showApple || showCompanySso;

  return (
    <div className="loginContainer">
      <Loader message="Logging in..." isVisible={loading} />
      <div className="loginSubContainer">
        <div className="logoContainer">
          <img src="/assets/images/zaviorLogo.png" alt="Zavior Logo" />
        </div>

        <p className="loginText">{isTrinityPortal() ? 'Login to Zavior Admin Portal' : 'Login to your Zavior account'}</p>
        {showProviderSignIn ? (
          <>
            <div className="oauthButtonStack">
              {showGoogle ? (
                <Button type="button" variant="outlined" className="oauthButton" onClick={() => startProvider('google')}>
                  Continue with Google
                </Button>
              ) : null}
              {showMicrosoft ? (
                <Button type="button" variant="outlined" className="oauthButton" onClick={() => startProvider('microsoft')}>
                  Continue with Microsoft
                </Button>
              ) : null}
              {showApple ? (
                <Button type="button" variant="outlined" className="oauthButton" onClick={() => startProvider('apple')}>
                  Continue with Apple
                </Button>
              ) : null}
              {showCompanySso ? (
                <Button type="button" variant="text" className="ssoButton" onClick={handleCompanySSO}>
                  Company SSO
                </Button>
              ) : null}
            </div>
            <div className="loginDivider"><span>or sign in with email</span></div>
          </>
        ) : null}
        <form onSubmit={handleSubmit}>
          <div className="fieldContainer">
            {/* Email Address Field */}
            <Box sx={fieldStyles.container}>
              <Typography sx={fieldStyles.label}>
                Email Address
              </Typography>
              <TextField
                variant="outlined"
                fullWidth
                placeholder="Enter your email address"
                value={domain}
                onChange={handleEmailChange}
                error={!!error}
                helperText={error}
                sx={{
                  ...fieldStyles.textField,
                  '& .MuiOutlinedInput-root.Mui-error fieldset': {
                    borderColor: '#EF4444',
                  },
                }}
                InputProps={{
                  autoComplete: "off",
                }}
              />
            </Box>
            {/* Password Field */}
            <Box sx={fieldStyles.container}>
              <Typography sx={fieldStyles.label}>
                Password
              </Typography>
              <TextField
                type={showPassword ? "text" : "password"}
                variant="outlined"
                fullWidth
                placeholder="Enter your password"
                value={password || ""}
                onChange={handlePasswordChange}
                sx={fieldStyles.textField}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleTogglePasswordVisibility}
                        edge="end"
                        sx={{ color: '#7E8299' }}
                      >
                        {showPassword ? (
                          <img
                            src="/assets/icons/eyeOutlineCross.png"
                            alt="Hide password"
                            style={{ width: '20px', height: '20px' }}
                          />
                        ) : (
                          <img
                            src="/assets/icons/eyeOutlineOpen.png"
                            alt="Show password"
                            style={{ width: '20px', height: '20px' }}
                          />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </div>
          <div className="loginActions">
            <Button
              type="submit"
              disabled={error || !filledUp}
              variant="contained"
              className={
                error || !filledUp ? "loginButtonDisable" : "loginButton"
              }
            >
              Log in
            </Button>
            <div
              style={{ justifyContent: "flex-end" }}
              onClick={() => handleForgetPassword()}
            >
              <Button variant="text" className="forgotPassword">
                Forgot Password?
              </Button>
            </div>
          </div>
        </form>

        {!isTrinityPortal() && (
        <p className="signupText">
          Don't have a Zavior account?{" "}
          <span className="signupLink" onClick={handleUserRegistration}>
            Get started here
          </span>
        </p>
        )}

        {/* Footer section */}
        <footer className="footer">
          {/*<a href="/privacy" className="footerLink" target="_blank" rel="noopener noreferrer">
            Privacy
          </a>
          <a href="/terms" className="footerLink" target="_blank" rel="noopener noreferrer">
            Terms
          </a>*/}
          <a href="https://www.zavior.ai/company/privacy" className="footerLink" target="_blank" rel="noopener noreferrer">
            Privacy
          </a>
          <a href="https://www.zavior.ai/company/terms" className="footerLink" target="_blank" rel="noopener noreferrer">
            Terms
          </a>
          <span>© 2026 Zavior</span>
        </footer>
      </div>
    </div>
    
  );
};

export default LoginModule;
